import { estimateRegionSizeBytes } from "@/modes/ipx-order-mode/ipx-size-estimate";
import { createSelector } from "@reduxjs/toolkit";
import { selectClippingBox } from "../clipping-box-selectors";
import { RootState } from "../store";
import { IpxOrderModeState } from "./ipx-order-mode-slice";

/**
 * @param state The app state
 * @returns the state of the IPX ordering wizard
 */
export function selectIpxOrderMode(state: RootState): IpxOrderModeState {
  return state.ipxOrderMode;
}

/** @returns the estimated export size of the selected region in bytes, or undefined while the point cloud is loading */
export const selectIpxEstimatedRegionBytes = createSelector(
  [selectIpxOrderMode, selectClippingBox],
  (
    {
      regionMeasures,
      pointCloudSize,
      pointCloudTotalBytes,
      totalScanCount,
      sentScans,
    },
    clippingBox,
  ) => {
    if (regionMeasures?.export) return regionMeasures.export.bytes;
    if (!pointCloudSize || pointCloudTotalBytes === undefined) return;
    // Assumes the scans contribute evenly to the dataset point cloud
    const scanShare = totalScanCount ? sentScans.length / totalScanCount : 1;
    return estimateRegionSizeBytes(
      pointCloudTotalBytes * scanShare,
      pointCloudSize,
      clippingBox?.size,
    );
  },
);

/**
 * @param state The app state
 * @returns the volume of the region box in cubic meters, or undefined without a box
 */
export function selectIpxRegionVolume(state: RootState): number | undefined {
  const size =
    selectClippingBox(state)?.size ?? state.ipxOrderMode.pointCloudSize;
  return size ? size[0] * size[1] * size[2] : undefined;
}

/**
 * @param state The app state
 * @returns true if leaving the wizard would discard the user selections
 */
export function selectIpxOrderHasProgress(state: RootState): boolean {
  return state.wizard.furthestStepIndex > 0 && !state.ipxOrderMode.order;
}

/**
 * @param state The app state
 * @returns how many scans are sent, undefined if the dataset is sent as a whole
 */
export function selectIpxSentScanCount(state: RootState): number | undefined {
  const { totalScanCount, sentScans } = state.ipxOrderMode;
  return totalScanCount === undefined ? undefined : sentScans.length;
}

/**
 * @param state The app state
 * @returns the plan area of the walkable levels sent in square meters, the area IPX models, undefined until estimated
 */
export function selectIpxRegionPlanArea(state: RootState): number | undefined {
  return state.ipxOrderMode.regionMeasures?.planAreaM2;
}
